import { FC, useMemo } from "react";
import {
  PatientBillInfoDto,
  TimelineTransactionDto,
  TimelineTransactionDtoMethodEnum,
  TimelineRechnungDto,
} from "../../../../api-client";
import {
  AlertTriangle,
  ArrowDownLeft,
  Building2,
  FileText,
  Loader2,
  Undo2,
  X,
  type LucideIcon,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRxBillingTimeline } from "../../../../stores/Transactions/transaction.queries";
import { formatDateUI } from "../../../../helpers/dateFormat";
import RxFeeWaivers from "../components/RxFeeWaivers";

interface BillDetailsModalWithMeta {
  open: boolean;
  onClose: () => void;
  metaDataBill: PatientBillInfoDto;
  rxId?: never;
  rxNumber?: never;
}

interface BillDetailsModalWithRxId {
  open: boolean;
  onClose: () => void;
  metaDataBill?: never;
  rxId: string;
  rxNumber?: string;
}

type BillDetailsModalProps = BillDetailsModalWithMeta | BillDetailsModalWithRxId;

const methodLabels: Record<string, string> = {
  CASH: "Bar",
  EC: "EC-Karte",
  LASTSCHRIFT: "Lastschrift",
  BANKTRANSFER: "Überweisung",
  VERSICHERUNG: "Versicherung",
};

/**
 * German labels for the insurance bill status.
 * - START: bill created but not yet exported
 * - OPEN: bill exported to the insurance company
 * - PREVIOUS: payment received / bill closed
 */
const billStatusLabels: Record<string, string> = {
  START: "Erstellt",
  OPEN: "Exportiert",
  PREVIOUS: "Abgeschlossen",
};

/** Tailwind classes for each bill status badge. */
const billStatusBadgeClasses: Record<string, string> = {
  START: "border-slate-200 bg-slate-100 text-slate-600",
  OPEN: "border-sky-200 bg-sky-100 text-sky-700",
  PREVIOUS: "border-emerald-200 bg-emerald-100 text-emerald-700",
};

/**
 * German labels for the insurance bill type.
 */
const billTypeLabels: Record<string, string> = {
  PAPER: "Papier Abrechnung",
  GKV: "GKV digitale Abrechnung",
  ZUZAHLUNG: "GKV Zuzahlungsforderung",
  BG: "BG Abrechnung",
  CORRECTION: "GKV Korrekturverfahren",
  LINE_ITEM_CORRECTION: "GKV Positionskorrektur",
};

/** Visual tone presets mapped to Tailwind utility classes. */
type Tone = "emerald" | "red" | "orange" | "violet" | "blue" | "amber";

const toneStyles: Record<Tone, { bubble: string; icon: string; line: string }> = {
  emerald: { bubble: "bg-emerald-100", icon: "text-emerald-600", line: "bg-emerald-200" },
  red: { bubble: "bg-red-100", icon: "text-red-600", line: "bg-red-200" },
  orange: { bubble: "bg-orange-100", icon: "text-orange-600", line: "bg-orange-200" },
  violet: { bubble: "bg-violet-100", icon: "text-violet-600", line: "bg-violet-200" },
  blue: { bubble: "bg-sky-100", icon: "text-sky-700", line: "bg-sky-200" },
  amber: { bubble: "bg-amber-100", icon: "text-amber-600", line: "bg-amber-200" },
};

type EventBadge = { label: string; className?: string };

type TimelineEvent = {
  id: string;
  date: string;
  label: string;
  sublabel?: string;
  who?: string;
  amount?: number;
  tone: Tone;
  Icon: LucideIcon;
  strikethrough?: boolean;
  badges?: EventBadge[];
};

function buildTimeline(
  transactions: TimelineTransactionDto[],
  rechnungen: TimelineRechnungDto[],
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  for (const tx of transactions) {
    const method = methodLabels[tx.method] ?? tx.method;
    const who = tx.createdByFirstName
      ? `${tx.createdByFirstName} ${tx.createdByLastName ?? ""}`.trim()
      : undefined;

    // Insurance (Abrechnung) entries: no patient-facing amount, show bill type/status.
    if (tx.method === TimelineTransactionDtoMethodEnum.Versicherung) {
      const badges: EventBadge[] = [];
      if (tx.billType) {
        badges.push({
          label: billTypeLabels[tx.billType] ?? tx.billType,
          className: "border-violet-200 bg-violet-100 text-violet-700",
        });
      }
      if (tx.billStatus) {
        badges.push({
          label: billStatusLabels[tx.billStatus] ?? tx.billStatus,
          className:
            billStatusBadgeClasses[tx.billStatus] ?? "border-slate-200 bg-slate-100 text-slate-600",
        });
      }

      events.push({
        id: tx.id,
        date: tx.createdAt,
        label: "Abrechnung eingereicht",
        sublabel: tx.transactionNumber,
        who,
        tone: "violet",
        Icon: Building2,
        badges,
      });

      if (tx.deletedAt) {
        events.push({
          id: `${tx.id}-del`,
          date: tx.deletedAt as unknown as string,
          label: "Abrechnung storniert",
          sublabel: tx.transactionNumber ? `${tx.transactionNumber}` : undefined,
          tone: "red",
          Icon: X,
          strikethrough: true,
        });
      }

      continue;
    }

    let label: string;
    let tone: Tone;
    let Icon: LucideIcon;

    if (tx.type === "Stornierung") {
      label = "Stornierung";
      tone = "red";
      Icon = Undo2;
    } else if (tx.type === "Rückerstattung") {
      label = "Rückerstattung";
      tone = "orange";
      Icon = Undo2;
    } else {
      label = "Zahlung eingegangen";
      tone = "emerald";
      Icon = ArrowDownLeft;
    }

    events.push({
      id: tx.id,
      date: tx.createdAt,
      label,
      sublabel: [
        method,
        tx.transactionNumber && `${tx.transactionNumber}`,
        tx.originalTransactionId && `Storno für ${tx.text}`,
      ]
        .filter(Boolean)
        .join(" · "),
      who,
      amount: tx.amount,
      tone,
      Icon,
    });

    if (tx.deletedAt) {
      events.push({
        id: `${tx.id}-del`,
        date: tx.deletedAt as unknown as string,
        label: tx.type === "Rückerstattung" ? "Rückerstattung storniert" : "Lastschrift storniert",
        sublabel: tx.transactionNumber ? `#${tx.transactionNumber}` : undefined,
        tone: "red",
        Icon: X,
        strikethrough: true,
      });
    }
  }

  for (const r of rechnungen) {
    const who = r.createdByFirstName
      ? `${r.createdByFirstName} ${r.createdByLastName ?? ""}`.trim()
      : undefined;

    events.push({
      id: r.id,
      date: r.createdAt,
      label: `Rechnung erstellt`,
      sublabel: r.rechnungNumber,
      who,
      amount: r.amount,
      tone: "blue",
      Icon: FileText,
    });

    if (r.deletedAt) {
      events.push({
        id: `${r.id}-del`,
        date: r.deletedAt as unknown as string,
        label: `Rechnung storniert`,
        sublabel: r.rechnungNumber,
        tone: "red",
        Icon: X,
        strikethrough: true,
      });
    }

    for (const rem of r.reminders ?? []) {
      events.push({
        id: rem.id,
        date: rem.createdAt,
        label: `Mahnung (Stufe ${rem.level})`,
        sublabel: `Rechnung: ${r.rechnungNumber}, Gebühr: ${Number(rem.fee)
          .toFixed(2)
          .replace(".", ",")} €`,
        tone: "amber",
        Icon: AlertTriangle,
      });
    }
  }

  events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return events;
}

const fmt = (n: number) => Number(n).toFixed(2).replace(".", ",");

const fmtAccounting = (n: number) => {
  const abs = Math.abs(n).toFixed(2).replace(".", ",");
  return n < 0 ? `${abs} €` : `+${abs} €`;
};

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

const PatientBillDetailsModalShad: FC<BillDetailsModalProps> = (props) => {
  const { open, onClose } = props;
  const meta = props.metaDataBill ?? null;
  const resolvedRxId = meta?.rxId ?? props.rxId!;
  const resolvedRxNumber = meta?.rxNumber ?? props.rxNumber ?? "";

  const { data, isLoading } = useRxBillingTimeline(resolvedRxId, open);

  const timeline = useMemo(
    () => (data ? buildTimeline(data.transactions, data.rechnungen) : []),
    [data],
  );

  const price = meta
    ? meta.isEstimated && meta.isTimeSlotZero
      ? meta.estimatedPrice
      : meta.realPrice
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="z-[1400] flex max-h-[90vh] w-[1000px] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 [&>button]:right-3 [&>button]:top-3 [&>button]:rounded-full [&>button]:bg-white/15 [&>button]:p-1 [&>button]:text-white [&>button]:opacity-100 [&>button]:hover:bg-white/30 [&>button>svg]:h-5 [&>button>svg]:w-5">
        <DialogTitle className="sr-only">Rechnungsdetails</DialogTitle>
        <DialogDescription className="sr-only">
          Aktivitäten und Zahlungen für dieses Rezept.
        </DialogDescription>

        {/* ─── Header (dark gradient) ─── */}
        <div className="relative flex-shrink-0 bg-gradient-to-br from-[#367b9a] to-[#374c67] px-6 pb-4 pt-5 text-white">
          {/* Top row: Rezept identity (left) + price (right) */}
          <div className="flex items-start justify-between gap-6 pr-8">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/70">
                Rezept
              </p>
              <div className="flex items-baseline gap-2.5">
                {meta?.date && (
                  <span className="text-2xl font-bold leading-none">{formatDateUI(meta.date)}</span>
                )}
                <span className="text-base font-medium text-white/80">{resolvedRxNumber}</span>
              </div>
            </div>

            {price !== null && (
              <div className="text-right">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-white/70">
                  Gesamtpreis
                </p>
                <div className="flex items-baseline justify-end gap-2">
                  <span className="text-3xl font-bold leading-none tabular-nums">
                    {fmt(price)} €
                  </span>
                  {meta?.isProvisionalPrice && (
                    <span className="text-sm font-medium text-white/70">geschätzt</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {meta && (
            <div className="mt-4 flex items-stretch gap-5 border-t border-white/15 pt-3">
              <MetaStat label="Bezahlt" value={`${fmt(meta.amountPaid)} €`} />
              <Separator orientation="vertical" className="h-auto bg-white/25" />
              <MetaStat label="Offen" value={`${fmt(meta.dueAmount)} €`} />
              <Separator orientation="vertical" className="h-auto bg-white/25" />
              <MetaStat label="Therapien" value={meta.therapies.map((t) => t.therapy).join(", ")} />
            </div>
          )}
        </div>

        {/* ─── Waivable automatic fees (private Rx only) ─── */}
        <RxFeeWaivers rxId={resolvedRxId} enabled={open} />

        {/* ─── Timeline content ─── */}
        <div className="flex flex-col bg-[#fafafab4] px-5 py-4">
          <p className="mb-3 flex-shrink-0 text-xs font-bold uppercase tracking-wide text-slate-500">
            Aktivität
          </p>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : timeline.length === 0 ? (
            <p className="py-1 text-base font-medium text-slate-600">
              Noch keine Aktivität für dieses Rezept.
            </p>
          ) : (
            <ScrollArea className="h-[550px] pr-3">
              <TooltipProvider delayDuration={200}>
                <ol className="relative">
                  {timeline.map((event, idx) => {
                    const isLast = idx === timeline.length - 1;
                    const tone = toneStyles[event.tone];
                    return (
                      <li key={event.id} className="flex">
                        {/* Date column */}
                        <div className="w-[88px] flex-shrink-0 pr-3 pt-1 text-right">
                          <span className="whitespace-nowrap text-[0.95rem] font-bold text-slate-600">
                            {formatDateUI(event.date)}
                          </span>
                        </div>

                        {/* Rail */}
                        <div className="flex flex-col items-center">
                          <div
                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${tone.bubble}`}
                          >
                            <event.Icon className={`h-[1.15rem] w-[1.15rem] ${tone.icon}`} />
                          </div>
                          {!isLast && <div className={`w-0.5 flex-1 rounded ${tone.line}`} />}
                        </div>

                        {/* Content */}
                        <div className={`flex-1 pl-3 ${isLast ? "pb-0" : "pb-4"}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p
                                className={`text-lg font-semibold leading-tight ${
                                  event.strikethrough
                                    ? "text-red-600 line-through"
                                    : "text-slate-900"
                                }`}
                              >
                                {event.label}
                              </p>

                              {(event.sublabel || event.who) && (
                                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 leading-tight">
                                  {event.sublabel && (
                                    <span className="text-[0.95rem] font-medium text-slate-600">
                                      {event.sublabel}
                                    </span>
                                  )}

                                  {event.sublabel && event.who && (
                                    <span className="text-slate-300">·</span>
                                  )}

                                  {event.who && (
                                    <span className="flex items-center gap-1.5 text-[0.95rem] font-medium text-slate-500">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Avatar className="h-6 w-6">
                                            <AvatarFallback className="bg-slate-300 text-[0.65rem] font-bold text-slate-700">
                                              {initials(event.who)}
                                            </AvatarFallback>
                                          </Avatar>
                                        </TooltipTrigger>
                                        <TooltipContent>{event.who}</TooltipContent>
                                      </Tooltip>
                                      {event.who}
                                    </span>
                                  )}
                                </div>
                              )}

                              {event.badges && event.badges.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {event.badges.map((b) => (
                                    <Badge
                                      key={b.label}
                                      variant="outline"
                                      className={`text-[0.8rem] font-semibold ${b.className ?? ""}`}
                                    >
                                      {b.label}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>

                            {event.amount !== undefined && (
                              <span
                                className={`ml-2 whitespace-nowrap text-lg font-bold tabular-nums ${
                                  event.amount < 0 ? "text-red-600" : "text-slate-900"
                                }`}
                              >
                                {fmtAccounting(event.amount)}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </TooltipProvider>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Header stat item */
const MetaStat: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide leading-none text-white/75">
      {label}
    </p>
    <p className="text-base font-semibold leading-tight text-white">{value}</p>
  </div>
);

export default PatientBillDetailsModalShad;
